/**
 * The learner's own unfinished practice sessions (left `active` by a crashed
 * run or a closed tab), each with "Discard unfinished session".
 *
 * Discarding calls POST /practice/sessions/:id/discard: the session becomes
 * cancelled, is never scored and never counts as an attempt. The session
 * currently being recorded on this page is never offered.
 */
import { useState } from 'react';
import {
  useDiscardPracticeSessionMutation,
  useListPracticeSessionsQuery,
  type PracticeSession,
} from '../../store/api/practiceApi';

export const DISCARD_LABEL = 'Discard unfinished session';
export const DISCARD_NOTE = 'A discarded session is not scored and never counts as an attempt.';

const TYPE_LABELS: Record<string, string> = {
  npc_practice: 'Practice call',
  free_practice: 'Free practice',
  scenario: 'Scenario practice',
  // Retired type: shown only for historical rows (nothing creates it any more).
  daily_challenge: 'Daily challenge (retired)',
  assessment: 'Assessment',
  training: 'Training',
};

export function sessionTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? 'Practice session';
}

function startedLabel(s: Pick<PracticeSession, 'startedAt' | 'createdAt'>): string {
  const at = new Date(s.startedAt || s.createdAt);
  return Number.isNaN(at.getTime()) ? '' : `Started ${at.toLocaleString()}`;
}

export function UnfinishedPracticeSessions({ excludeSessionId }: { excludeSessionId?: string | null }) {
  const { data } = useListPracticeSessionsQuery({ status: 'active', limit: 20 });
  const [discard, { isLoading }] = useDiscardPracticeSessionMutation();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessions = (data?.data ?? []).filter((s) => s.id !== excludeSessionId && s.status === 'active');
  if (sessions.length === 0) return null;

  const onDiscard = async (id: string) => {
    setError(null);
    try {
      await discard(id).unwrap();
      setConfirmingId(null);
    } catch {
      setError('That session could not be discarded. Please try again.');
    }
  };

  return (
    <section className="card p-5 space-y-3" aria-labelledby="unfinished-sessions-heading" data-testid="unfinished-sessions">
      <div>
        <h2 id="unfinished-sessions-heading" className="text-base font-semibold text-gray-900">
          Unfinished sessions
        </h2>
        <p className="text-sm text-gray-500">{DISCARD_NOTE}</p>
      </div>
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <ul className="divide-y divide-gray-100">
        {sessions.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-2" data-session-id={s.id}>
            <div>
              <p className="text-sm font-medium text-gray-800">{sessionTypeLabel(s.type)}</p>
              <p className="text-xs text-gray-500">{startedLabel(s)}</p>
            </div>
            {confirmingId === s.id ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Discard this session?</span>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                  disabled={isLoading}
                  onClick={() => onDiscard(s.id)}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                  onClick={() => setConfirmingId(null)}
                >
                  Keep
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                onClick={() => setConfirmingId(s.id)}
              >
                {DISCARD_LABEL}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
